import type { BatchItem } from "../overlay/BatchPanel";
import type { ScanMode } from "../overlay/ModeSwitch";
import type { OfflineScanItem, PriceCompareResponse } from "../types";
import type { Language } from "../i18n";

export interface LocalScanHistoryItem {
  id: string;
  barcode: string;
  timestamp: number;
  name?: string;
  lookedUp?: boolean;
  lastResult?: PriceCompareResponse;
}

const MAX_SCAN_HISTORY_ITEMS = 20;

export const STORAGE_KEYS = {
  BATCH_ITEMS: "fit_scanner_batch_items",
  SCAN_MODE: "fit_scanner_scan_mode",
  CURRENT_STORE: "fit_scanner_current_store",
  START_TOKEN: "fit_scanner_start_token",
  SOUND_ENABLED: "fit_scanner_sound_enabled",
  OFFLINE_QUEUE: "fit_scanner_offline_queue",
  SCAN_HISTORY: "fit_scanner_scan_history",
  LANGUAGE: "fit_scanner_language",
} as const;

/**
 * Retrieves saved batch scanned items from localStorage.
 * Returns empty array on parse failure or missing data.
 */
export function getSavedBatchItems(): BatchItem[] {
  if (typeof window === "undefined" || !window.localStorage) return [];
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.BATCH_ITEMS);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter(
        (item): item is BatchItem =>
          typeof item === "object" &&
          item !== null &&
          typeof item.id === "string" &&
          typeof item.barcode === "string" &&
          item.barcode.length > 0,
      )
      .map((item) => {
        const res: BatchItem = {
          id: item.id,
          barcode: item.barcode,
        };
        if (typeof item.name === "string") res.name = item.name;
        if (typeof item.price === "number" && !isNaN(item.price)) res.price = item.price;
        if (typeof item.currency === "string") res.currency = item.currency;
        if (typeof item.quantity === "number" && item.quantity > 0) {
          res.quantity = Math.floor(item.quantity);
        }
        return res;
      });
  } catch {
    return [];
  }
}

/**
 * Persists batch scanned items to localStorage.
 */
export function saveBatchItems(items: BatchItem[]): void {
  if (typeof window === "undefined" || !window.localStorage) return;
  try {
    if (items.length === 0) {
      localStorage.removeItem(STORAGE_KEYS.BATCH_ITEMS);
    } else {
      localStorage.setItem(STORAGE_KEYS.BATCH_ITEMS, JSON.stringify(items));
    }
  } catch {
    // Safe degradation if localStorage is restricted or quota exceeded
  }
}

/**
 * Clears all saved batch scanned items from localStorage.
 */
export function clearSavedBatchItems(): void {
  if (typeof window === "undefined" || !window.localStorage) return;
  try {
    localStorage.removeItem(STORAGE_KEYS.BATCH_ITEMS);
  } catch {
    // Safe degradation
  }
}

/**
 * Retrieves saved scan mode ("single" | "batch").
 */
export function getSavedScanMode(): ScanMode | null {
  if (typeof window === "undefined" || !window.localStorage) return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.SCAN_MODE);
    if (raw === "single" || raw === "batch") return raw;
    return null;
  } catch {
    return null;
  }
}

/**
 * Persists scan mode to localStorage.
 */
export function saveScanMode(mode: ScanMode): void {
  if (typeof window === "undefined" || !window.localStorage) return;
  try {
    localStorage.setItem(STORAGE_KEYS.SCAN_MODE, mode);
  } catch {
    // Safe degradation
  }
}

/**
 * Retrieves saved store ID.
 */
export function getSavedStoreId(): string | undefined {
  if (typeof window === "undefined" || !window.localStorage) return undefined;
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.CURRENT_STORE);
    return raw || undefined;
  } catch {
    return undefined;
  }
}

/**
 * Persists store ID to localStorage.
 */
export function saveStoreId(storeId: string | undefined): void {
  if (typeof window === "undefined" || !window.localStorage) return;
  try {
    if (!storeId) {
      localStorage.removeItem(STORAGE_KEYS.CURRENT_STORE);
    } else {
      localStorage.setItem(STORAGE_KEYS.CURRENT_STORE, storeId);
    }
  } catch {
    // Safe degradation
  }
}

/**
 * Retrieves saved session start token.
 */
export function getSavedStartToken(): string | null {
  if (typeof window === "undefined" || !window.localStorage) return null;
  try {
    return localStorage.getItem(STORAGE_KEYS.START_TOKEN) || null;
  } catch {
    return null;
  }
}

/**
 * Persists session start token to localStorage.
 */
export function saveStartToken(token: string): void {
  if (typeof window === "undefined" || !window.localStorage) return;
  try {
    localStorage.setItem(STORAGE_KEYS.START_TOKEN, token);
  } catch {
    // Safe degradation
  }
}

/**
 * Clears saved session start token from localStorage.
 */
export function clearSavedStartToken(): void {
  if (typeof window === "undefined" || !window.localStorage) return;
  try {
    localStorage.removeItem(STORAGE_KEYS.START_TOKEN);
  } catch {
    // Safe degradation
  }
}

/**
 * Retrieves saved sound preference from localStorage (defaults to true).
 */
export function getSavedSoundEnabled(): boolean {
  if (typeof window === "undefined" || !window.localStorage) return true;
  try {
    const saved = localStorage.getItem(STORAGE_KEYS.SOUND_ENABLED);
    return saved !== null ? saved === "true" : true;
  } catch {
    return true;
  }
}

/**
 * Persists sound preference to localStorage.
 */
export function saveSoundEnabled(enabled: boolean): void {
  if (typeof window === "undefined" || !window.localStorage) return;
  try {
    localStorage.setItem(STORAGE_KEYS.SOUND_ENABLED, String(enabled));
  } catch {
    // Safe degradation
  }
}

/**
 * Retrieves saved offline scan queue items from localStorage.
 * Returns empty array on parse failure or missing data.
 */
export function getSavedOfflineQueue(): OfflineScanItem[] {
  if (typeof window === "undefined" || !window.localStorage) return [];
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.OFFLINE_QUEUE);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (item): item is OfflineScanItem =>
        typeof item === "object" &&
        item !== null &&
        typeof item.id === "string" &&
        typeof item.barcode === "string" &&
        item.barcode.length > 0 &&
        typeof item.timestamp === "number",
    );
  } catch {
    return [];
  }
}

/**
 * Persists offline scan queue items to localStorage.
 */
export function saveOfflineQueue(items: OfflineScanItem[]): void {
  if (typeof window === "undefined" || !window.localStorage) return;
  try {
    if (items.length === 0) {
      localStorage.removeItem(STORAGE_KEYS.OFFLINE_QUEUE);
    } else {
      localStorage.setItem(STORAGE_KEYS.OFFLINE_QUEUE, JSON.stringify(items));
    }
  } catch {
    // Safe degradation
  }
}

/**
 * Clears all saved offline scan queue items from localStorage.
 */
export function clearSavedOfflineQueue(): void {
  if (typeof window === "undefined" || !window.localStorage) return;
  try {
    localStorage.removeItem(STORAGE_KEYS.OFFLINE_QUEUE);
  } catch {
    // Safe degradation
  }
}

/**
 * Retrieves saved language preference from localStorage.
 */
export function getSavedLanguage(): Language | null {
  if (typeof window === "undefined" || !window.localStorage) return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.LANGUAGE);
    if (raw === "ru" || raw === "et" || raw === "en") return raw;
    return null;
  } catch {
    return null;
  }
}

/**
 * Persists language preference to localStorage.
 */
export function saveLanguage(language: Language): void {
  if (typeof window === "undefined" || !window.localStorage) return;
  try {
    localStorage.setItem(STORAGE_KEYS.LANGUAGE, language);
  } catch {
    // Safe degradation
  }
}

/**
 * Clears saved language preference from localStorage.
 */
export function clearSavedLanguage(): void {
  if (typeof window === "undefined" || !window.localStorage) return;
  try {
    localStorage.removeItem(STORAGE_KEYS.LANGUAGE);
  } catch {
    // Safe degradation
  }
}

function isPriceCompareResponse(value: unknown): value is PriceCompareResponse {
  if (!value || typeof value !== "object") return false;
  const result = value as PriceCompareResponse;
  return (
    typeof result.product?.id === "string" &&
    typeof result.product?.name === "string" &&
    typeof result.product?.barcode === "string" &&
    Array.isArray(result.prices) &&
    typeof result.best?.storeId === "string" &&
    typeof result.best?.price === "number"
  );
}

function isHistoryItem(value: unknown): value is LocalScanHistoryItem {
  if (!value || typeof value !== "object") return false;
  const item = value as Record<string, unknown>;
  return (
    typeof item.id === "string" &&
    typeof item.barcode === "string" &&
    item.barcode.length > 0 &&
    typeof item.timestamp === "number"
  );
}

/**
 * Retrieves local scan history (newest first). Used for leftover offline
 * codes that should not auto-sync — the user searches them on demand.
 */
export function getSavedScanHistory(): LocalScanHistoryItem[] {
  if (typeof window === "undefined" || !window.localStorage) return [];
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.SCAN_HISTORY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isHistoryItem).map((item) => ({
      id: item.id,
      barcode: item.barcode,
      timestamp: item.timestamp,
      ...(typeof item.name === "string" ? { name: item.name } : {}),
      ...(typeof item.lookedUp === "boolean" ? { lookedUp: item.lookedUp } : {}),
      ...(isPriceCompareResponse(item.lastResult) ? { lastResult: item.lastResult } : {}),
    }));
  } catch {
    return [];
  }
}

/**
 * Persists local scan history. Newest-first, unique by barcode, max 20.
 */
export function saveScanHistory(items: LocalScanHistoryItem[]): void {
  if (typeof window === "undefined" || !window.localStorage) return;
  try {
    const seen = new Set<string>();
    const unique: LocalScanHistoryItem[] = [];
    for (const item of items) {
      const barcode = item.barcode.trim();
      if (!barcode || seen.has(barcode)) continue;
      seen.add(barcode);
      unique.push({ ...item, barcode });
      if (unique.length >= MAX_SCAN_HISTORY_ITEMS) break;
    }
    if (unique.length === 0) {
      localStorage.removeItem(STORAGE_KEYS.SCAN_HISTORY);
    } else {
      localStorage.setItem(STORAGE_KEYS.SCAN_HISTORY, JSON.stringify(unique));
    }
  } catch {
    // Safe degradation
  }
}

/** Upserts a barcode at the top of local scan history. */
export function upsertScanHistoryItem(
  barcode: string,
  patch?: { name?: string; lookedUp?: boolean; lastResult?: PriceCompareResponse },
): LocalScanHistoryItem[] {
  const trimmed = barcode.trim();
  if (!trimmed) return getSavedScanHistory();
  const now = Date.now();
  const existing = getSavedScanHistory();
  const previous = existing.find((item) => item.barcode === trimmed);
  const next: LocalScanHistoryItem = {
    id: previous?.id ?? `${now}-${Math.random().toString(36).slice(2, 8)}`,
    barcode: trimmed,
    timestamp: now,
    name: patch?.name ?? previous?.name,
    lookedUp: patch?.lookedUp ?? previous?.lookedUp,
    lastResult: patch?.lastResult ?? previous?.lastResult,
  };
  const rest = existing.filter((item) => item.barcode !== trimmed);
  const updated = [next, ...rest];
  saveScanHistory(updated);
  return updated;
}

/**
 * Moves leftover offline-queue codes into local history (deduped) and
 * clears the obsolete sync queue so it never auto-drains again.
 */
export function migrateOfflineQueueToHistory(): LocalScanHistoryItem[] {
  const queued = getSavedOfflineQueue();
  if (queued.length === 0) {
    return getSavedScanHistory();
  }
  const newestFirst = [...queued].sort((a, b) => b.timestamp - a.timestamp);
  for (const item of newestFirst) {
    upsertScanHistoryItem(item.barcode);
  }
  clearSavedOfflineQueue();
  return getSavedScanHistory();
}


